/**
 * Date Validation Service - Centralized rental date business rules
 * 
 * Validates rental dates against business rules and provides helpful error messages.
 * Handles edge cases like stale cart dates, insufficient lead time, and duration limits.
 * 
 * BUSINESS RULES:
 * - Minimum lead time: 2 BUSINESS days, evaluated against a 2:00 PM Central cutoff
 *   (orders placed at/after 2:00 PM CT no longer count "today" as a lead day, and
 *   weekends/holidays are skipped).
 * - Friday at/after 2:00 PM CT and weekend orders require 3 BUSINESS days, making
 *   the following Wednesday the earliest rental start date.
 * - Delivery is due no later than 1 business day before the rental start.
 * - Maximum rental duration: 14 calendar days
 * - Dates cannot be in the past
 * 
 * Usage:
 *   const validation = DateValidationService.validate(startDate, endDate);
 *   if (!validation.valid) {
 *     console.error(validation.errors);
 *     // Show user-friendly message: validation.userMessage
 *   }
 */

class DateValidationService {
  /**
   * Business rules configuration
   */
  static BUSINESS_RULES = {
    MIN_LEAD_TIME_DAYS: 2,                  // Minimum lead time, counted in BUSINESS days
    WEEKEND_LEAD_TIME_DAYS: 3,              // Friday at/after cutoff and weekend orders
    DELIVERY_DAYS_BEFORE_START: 1,          // Delivery deadline before rental start
    MAX_RENTAL_DAYS: 14,                    // Cannot rent for more than 14 days
    USE_BUSINESS_DAYS: true,               // true = skip weekends (and holidays) for lead time
    CUTOFF_HOUR_CT: 14,                    // 2 PM Central: at/after this, same-day processing closes
    BUSINESS_TIMEZONE: 'America/Chicago',  // Pin lead-time math to Central (auto CST/CDT)
    OBSERVED_HOLIDAYS: []                  // 'YYYY-MM-DD' strings to skip, e.g. ['2026-12-25']
  };

  /**
   * Validate rental date range against business rules
   * 
   * @param {string} startDate - Start date (YYYY-MM-DD or M/D/YYYY format)
   * @param {string} endDate - End date (YYYY-MM-DD or M/D/YYYY format)
   * @param {number|null} productMaxDays - Optional product-specific max days constraint
   * @returns {object} Validation result
   * 
   * @example
   * const result = DateValidationService.validate('2024-03-15', '2024-03-20');
   * if (!result.valid) {
   *   alert(result.userMessage);
   * }
   */
  static validate(startDate, endDate, productMaxDays = null) {
    const result = {
      valid: true,
      errors: [],
      warnings: [],
      userMessage: null,
      suggestedFix: null,
      requiresCallSales: false,
      details: {
        startDate,
        endDate,
        duration: null,
        daysUntilStart: null,
        maxDays: productMaxDays || this.BUSINESS_RULES.MAX_RENTAL_DAYS,
        requiredLeadTimeDays: null,
        earliestStartDate: null,
        issues: []
      }
    };

    // Handle missing dates
    if (!startDate || !endDate) {
      result.valid = false;
      result.errors.push('MISSING_DATES');
      result.userMessage = 'Rental dates are required.';
      result.details.issues.push('missing_dates');
      return result;
    }

    // Parse dates (handle multiple formats)
    const start = this.parseDate(startDate);
    const end = this.parseDate(endDate);
    
    // Validate parsed dates
    if (!start || isNaN(start.getTime())) {
      result.valid = false;
      result.errors.push('INVALID_START_DATE');
      result.userMessage = `Invalid start date format: "${startDate}". Please select valid dates.`;
      result.details.issues.push('invalid_start_date');
      return result;
    }
    
    if (!end || isNaN(end.getTime())) {
      result.valid = false;
      result.errors.push('INVALID_END_DATE');
      result.userMessage = `Invalid end date format: "${endDate}". Please select valid dates.`;
      result.details.issues.push('invalid_end_date');
      return result;
    }
    
    // Earliest valid start date — single source of truth for all date consumers
    const centralNow = this.getCentralNow();
    const today = new Date(
      centralNow.getFullYear(),
      centralNow.getMonth(),
      centralNow.getDate()
    );
    const requiredLeadTimeDays = this.getRequiredLeadTimeDays(centralNow);
    const earliestStartDate = this.getEarliestStartDate(centralNow);
    const earliestStart = this.parseDate(earliestStartDate);
    result.details.requiredLeadTimeDays = requiredLeadTimeDays;
    result.details.earliestStartDate = earliestStartDate;

    // Calculate duration (inclusive)
    const duration = this.calculateDays(startDate, endDate);
    result.details.duration = duration;

    // Calculate days until start
    const daysUntilStart = Math.floor((start - today) / (1000 * 60 * 60 * 24));
    result.details.daysUntilStart = daysUntilStart;

    // Validation Check 1: Start date in the past
    if (start < today) {
      result.valid = false;
      result.errors.push('START_DATE_IN_PAST');
      result.details.issues.push('start_date_in_past');
      
      const daysPast = Math.abs(daysUntilStart);
      result.userMessage = `Your rental start date (${this.formatDateFriendly(startDate)}) is ${daysPast} day${daysPast !== 1 ? 's' : ''} in the past.\n\nWe need at least ${requiredLeadTimeDays} business days to prepare your equipment for delivery.`;
      
      // Suggest new dates
      result.suggestedFix = this.suggestValidDates(startDate, endDate);
    }

    // Validation Check 2: Insufficient lead time (today or later, but before the earliest valid start)
    if (start >= today && earliestStart && start < earliestStart) {
      result.valid = false;
      result.errors.push('INSUFFICIENT_LEAD_TIME');
      result.details.issues.push('insufficient_lead_time');
      
      result.userMessage = `Your rental start date (${this.formatDateFriendly(startDate)}) is too soon.\n\nWe need at least ${requiredLeadTimeDays} business days to confirm availability and prepare your equipment for delivery. The earliest available start date is ${this.formatDateFriendly(earliestStartDate)}.`;
      
      // Suggest new dates
      result.suggestedFix = this.suggestValidDates(startDate, endDate);
    }

    // Validation Check 3: End date before start date
    if (end < start) {
      result.valid = false;
      result.errors.push('END_BEFORE_START');
      result.details.issues.push('end_before_start');
      
      result.userMessage = `Your rental end date (${this.formatDateFriendly(endDate)}) is before the start date (${this.formatDateFriendly(startDate)}).\n\nPlease select valid dates.`;
    }

    // Validation Check 4 & 5: Duration exceeds maximum
    // Check product-specific max first (if exists), otherwise check global max
    // This prevents showing both errors when they're essentially the same issue
    const effectiveMaxDays = productMaxDays || this.BUSINESS_RULES.MAX_RENTAL_DAYS;
    
    if (duration > effectiveMaxDays) {
      result.valid = false;
      result.errors.push('DURATION_TOO_LONG');
      result.details.issues.push('duration_too_long');
      result.requiresCallSales = true;
      
      // Simple message for fallback/logging
      result.userMessage = `Your rental duration (${duration} days) exceeds the ${effectiveMaxDays}-day maximum for online checkout.`;
    }

    // If multiple issues, combine messages
    if (result.errors.length > 1) {
      const effectiveMaxDays = productMaxDays || this.BUSINESS_RULES.MAX_RENTAL_DAYS;
      
      const issuesList = result.details.issues.map(issue => {
        switch(issue) {
          case 'start_date_in_past': return `• Start date is in the past`;
          case 'insufficient_lead_time': return `• Insufficient lead time (need ${requiredLeadTimeDays} business days)`;
          case 'duration_too_long': return `• Duration exceeds ${effectiveMaxDays}-day maximum`;
          case 'end_before_start': return `• End date is before start date`;
          default: return `• ${issue}`;
        }
      }).join('\n');

      result.userMessage = `Your rental dates have multiple issues:\n\n${issuesList}\n\nNo problem! Our sales team can help you find the perfect solution with personalized service, better pricing, and flexible terms. Give us a call!`;
      result.requiresCallSales = true;
    }

    return result;
  }

  /**
   * Suggest valid date range based on invalid dates
   * Preserves original duration if possible, otherwise caps at max
   * 
   * @param {string} oldStartDate - Invalid start date (any format)
   * @param {string} oldEndDate - Invalid end date (any format)
   * @returns {object} Suggested date range
   */
  static suggestValidDates(oldStartDate, oldEndDate) {
    // Calculate original duration
    let originalDuration = this.calculateDays(oldStartDate, oldEndDate);
    
    // If duration calculation failed or is unreasonable, default to 7 days
    if (!originalDuration || originalDuration <= 0 || originalDuration > 365) {
      console.warn('⚠️ Invalid duration calculated, defaulting to 7 days');
      originalDuration = 7;
    }
    
    // Earliest valid start date (business-day aware, 2 PM CT cutoff)
    const earliestStart = this.parseDate(this.getEarliestStartDate());
    
    // Use original duration, but cap at MAX_RENTAL_DAYS
    const newDuration = Math.min(originalDuration, this.BUSINESS_RULES.MAX_RENTAL_DAYS);
    
    // Calculate new end date
    const newEnd = new Date(earliestStart);
    newEnd.setDate(newEnd.getDate() + newDuration - 1); // -1 because duration is inclusive
    
    const newStartDate = this.formatDateLocal(earliestStart);
    const newEndDate = this.formatDateLocal(newEnd);
    
    return {
      startDate: newStartDate,
      endDate: newEndDate,
      duration: newDuration,
      originalDuration,
      durationChanged: newDuration !== originalDuration,
      message: newDuration !== originalDuration
        ? `We adjusted your duration from ${originalDuration} to ${newDuration} days to fit within our ${this.BUSINESS_RULES.MAX_RENTAL_DAYS}-day maximum.`
        : `We moved your ${newDuration}-day rental to the next available dates.`
    };
  }

  /**
   * Parse date string into Date object
   * Handles multiple formats: YYYY-MM-DD, M/D/YYYY
   * 
   * @param {string} dateStr - Date string
   * @returns {Date|null} Parsed Date object or null if invalid
   */
  static parseDate(dateStr) {
    if (!dateStr) return null;
    
    // Try YYYY-MM-DD format first (ISO format)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const date = new Date(dateStr + 'T00:00:00');
      if (!isNaN(date.getTime())) return date;
    }
    
    // Try M/D/YYYY or MM/DD/YYYY format
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
      const [month, day, year] = dateStr.split('/');
      const date = new Date(year, parseInt(month) - 1, parseInt(day));
      if (!isNaN(date.getTime())) return date;
    }
    
    // Fallback: try native Date parsing
    const date = new Date(dateStr);
    return !isNaN(date.getTime()) ? date : null;
  }

  /**
   * Calculate days between two dates (inclusive)
   * 
   * @param {string} startDate - Start date (YYYY-MM-DD or M/D/YYYY)
   * @param {string} endDate - End date (YYYY-MM-DD or M/D/YYYY)
   * @returns {number} Number of days (inclusive)
   */
  static calculateDays(startDate, endDate) {
    const start = this.parseDate(startDate);
    const end = this.parseDate(endDate);
    
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.error('Invalid dates for calculation:', { startDate, endDate });
      return 0;
    }
    
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1; // Inclusive of both start and end
  }

  /**
   * Format date as YYYY-MM-DD
   * 
   * @param {Date} date - JavaScript Date object
   * @returns {string} ISO date string (YYYY-MM-DD)
   */
  static formatDateISO(date) {
    return date.toISOString().split('T')[0];
  }

  /**
   * Format date in user-friendly format
   * 
   * @param {string} dateStr - Date string (YYYY-MM-DD or M/D/YYYY)
   * @returns {string} Formatted date (e.g., "Mon, Mar 15, 2024")
   */
  static formatDateFriendly(dateStr) {
    const date = this.parseDate(dateStr);
    if (!date || isNaN(date.getTime())) {
      return dateStr; // Return original if parsing fails
    }
    const options = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }

  /**
   * Check if a date range is currently valid
   * Quick validation without detailed error messages
   * 
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   * @returns {boolean} True if valid
   */
  static isValid(startDate, endDate) {
    const result = this.validate(startDate, endDate);
    return result.valid;
  }

  /**
   * Get the current date/time as wall-clock in the business timezone (Central).
   *
   * The returned Date's local fields (year/month/date/hours) reflect Central time
   * regardless of where the shopper's browser is. This is the same technique used
   * in OfficeAvailabilityService and handles CST/CDT automatically.
   *
   * @returns {Date} Date whose fields equal the current Central wall-clock time
   */
  static getCentralNow() {
    const now = new Date();
    return new Date(
      now.toLocaleString('en-US', { timeZone: this.BUSINESS_RULES.BUSINESS_TIMEZONE })
    );
  }

  /**
   * Format a Date as YYYY-MM-DD using its LOCAL fields.
   *
   * Unlike formatDateISO (which goes through UTC and can shift the day for some
   * timezones), this preserves the calendar day represented by the Date's local
   * fields. Used for the lead-time floor where day accuracy is critical.
   *
   * @param {Date} date - JavaScript Date object
   * @returns {string} ISO date string (YYYY-MM-DD)
   */
  static formatDateLocal(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Determine whether a date is a business day (not a weekend or observed holiday).
   *
   * @param {Date} date - JavaScript Date object
   * @returns {boolean} True if the date is a business day
   */
  static isBusinessDay(date) {
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) return false; // Sunday / Saturday
    return !this.BUSINESS_RULES.OBSERVED_HOLIDAYS.includes(this.formatDateLocal(date));
  }

  /**
   * Move a date forward or backward by a number of business days.
   *
   * @param {Date} date - Starting calendar date
   * @param {number} businessDays - Positive to move forward, negative to move backward
   * @returns {Date} A new Date set to the resulting business day
   */
  static addBusinessDays(date, businessDays) {
    const result = new Date(date);
    const direction = businessDays >= 0 ? 1 : -1;
    let remaining = Math.abs(businessDays);

    while (remaining > 0) {
      result.setDate(result.getDate() + direction);
      if (this.isBusinessDay(result)) {
        remaining--;
      }
    }

    return result;
  }

  /**
   * Determine whether the weekend lead-time rule applies.
   *
   * @param {Date} centralNow - Current wall-clock date/time in Central time
   * @returns {boolean} True from the Friday cutoff through Sunday
   */
  static isWeekendLeadTimeWindow(centralNow = this.getCentralNow()) {
    const dayOfWeek = centralNow.getDay();
    const isFridayAfterCutoff =
      dayOfWeek === 5 && centralNow.getHours() >= this.BUSINESS_RULES.CUTOFF_HOUR_CT;
    return isFridayAfterCutoff || dayOfWeek === 6 || dayOfWeek === 0;
  }

  /**
   * Get the effective lead-time requirement for the current order window.
   *
   * @param {Date} centralNow - Current wall-clock date/time in Central time
   * @returns {number} Required number of business days
   */
  static getRequiredLeadTimeDays(centralNow = this.getCentralNow()) {
    if (!this.BUSINESS_RULES.USE_BUSINESS_DAYS) {
      return this.BUSINESS_RULES.MIN_LEAD_TIME_DAYS;
    }

    return this.isWeekendLeadTimeWindow(centralNow)
      ? this.BUSINESS_RULES.WEEKEND_LEAD_TIME_DAYS
      : this.BUSINESS_RULES.MIN_LEAD_TIME_DAYS;
  }

  /**
   * Get the earliest valid rental start date.
   *
   * Friday at/after the 2 PM CT cutoff and weekend orders start no earlier than the
   * following Wednesday. At other times, counts MIN_LEAD_TIME_DAYS business days
   * forward and applies the standard cutoff.
   *
   * @param {Date|null} centralNow - Optional Central wall-clock time for deterministic use
   * @returns {string} ISO date string (YYYY-MM-DD)
   *
   * @example
   * // Thursday 6:00 PM CT, MIN_LEAD_TIME_DAYS = 2
   * DateValidationService.getEarliestStartDate(); // Returns the following Tuesday
   */
  static getEarliestStartDate(centralNow = null) {
    const central = centralNow || this.getCentralNow();

    // Calendar-day fallback if business-day logic is disabled
    if (!this.BUSINESS_RULES.USE_BUSINESS_DAYS) {
      const earliest = new Date(
        central.getFullYear(),
        central.getMonth(),
        central.getDate()
      );
      earliest.setDate(earliest.getDate() + this.BUSINESS_RULES.MIN_LEAD_TIME_DAYS);
      return this.formatDateLocal(earliest);
    }

    // Anchor = today's calendar date in Central
    const anchor = new Date(central.getFullYear(), central.getMonth(), central.getDate());

    // Friday at/after cutoff and weekend orders require three full business days.
    if (this.isWeekendLeadTimeWindow(central)) {
      return this.formatDateLocal(
        this.addBusinessDays(anchor, this.BUSINESS_RULES.WEEKEND_LEAD_TIME_DAYS)
      );
    }

    // Past the 2 PM CT cutoff → today can no longer be used as a lead day
    if (central.getHours() >= this.BUSINESS_RULES.CUTOFF_HOUR_CT) {
      anchor.setDate(anchor.getDate() + 1);
    }

    return this.formatDateLocal(
      this.addBusinessDays(anchor, this.BUSINESS_RULES.MIN_LEAD_TIME_DAYS)
    );
  }

  /**
   * Check whether a selected start date is the earliest currently allowed date.
   *
   * @param {string} startDateString - Rental start date (YYYY-MM-DD or M/D/YYYY)
   * @param {Date|null} centralNow - Optional Central wall-clock time for deterministic use
   * @returns {boolean} True when the selected date matches the current lead-time floor
   */
  static isEarliestAllowedStartDate(startDateString, centralNow = null) {
    const startDate = this.parseDate(startDateString);
    if (!startDate || isNaN(startDate.getTime())) {
      return false;
    }

    return (
      this.formatDateLocal(startDate) ===
      this.getEarliestStartDate(centralNow)
    );
  }

  /**
   * Get the maximum allowed end date for a given start date
   * 
   * @param {string} startDate - Start date (YYYY-MM-DD or M/D/YYYY)
   * @returns {string} ISO date string (YYYY-MM-DD)
   */
  static getMaxEndDate(startDate) {
    const start = this.parseDate(startDate);
    if (!start || isNaN(start.getTime())) {
      return null;
    }
    const maxEnd = new Date(start);
    maxEnd.setDate(maxEnd.getDate() + this.BUSINESS_RULES.MAX_RENTAL_DAYS - 1); // -1 because inclusive
    return this.formatDateISO(maxEnd);
  }

  /**
   * Calculate the delivery deadline based on rental start date.
   * 
   * Delivery is due no later than DELIVERY_DAYS_BEFORE_START business days
   * before the rental begins.
   * 
   * @param {string} startDateString - Rental start date (YYYY-MM-DD or M/D/YYYY)
   * @returns {string} ISO date string (YYYY-MM-DD) for delivery deadline
   */
  static calculateDeliveryDate(startDateString) {
    const startDate = this.parseDate(startDateString);
    
    if (!startDate || isNaN(startDate.getTime())) {
      console.error('Invalid start date for delivery calculation:', startDateString);
      return null;
    }
    
    const deliveryDate = this.addBusinessDays(
      startDate,
      -this.BUSINESS_RULES.DELIVERY_DAYS_BEFORE_START
    );

    return this.formatDateLocal(deliveryDate);
  }

  /**
   * Check if rental start date is next business day (requires urgent attention)
   * Returns true if the rental starts tomorrow, indicating user should call
   * 
   * @param {string} startDateString - Rental start date (YYYY-MM-DD or M/D/YYYY)
   * @returns {boolean} True if rental starts tomorrow
   * 
   * @example
   * // Today is Monday, April 20th
   * DateValidationService.isNextDayRental('2026-04-21'); // Returns true
   * DateValidationService.isNextDayRental('2026-04-22'); // Returns false
   */
  static isNextDayRental(startDateString) {
    const startDate = this.parseDate(startDateString);
    
    if (!startDate || isNaN(startDate.getTime())) {
      return false;
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    return startDate.getTime() === tomorrow.getTime();
  }

  /**
   * Calculate return date (1 business day after rental end date)
   * 
   * Ensures equipment is returned on the next business day after the rental period ends.
   * Handles weekend returns appropriately:
   * - Friday end → Monday return (skip weekend)
   * - Saturday end → Monday return (skip Sunday)
   * - Sunday end → Monday return
   * - Weekday end → Next day return
   * 
   * @param {string} endDateString - Rental end date (YYYY-MM-DD or M/D/YYYY)
   * @returns {string} ISO date string (YYYY-MM-DD) for return date
   * 
   * @example
   * // End on Friday, April 25th
   * DateValidationService.calculateReturnDate('2026-04-25');
   * // Returns '2026-04-28' (Monday, skipping weekend)
   * 
   * // End on Wednesday, April 22nd
   * DateValidationService.calculateReturnDate('2026-04-22');
   * // Returns '2026-04-23' (Thursday, next day)
   */
  static calculateReturnDate(endDateString) {
    const endDate = this.parseDate(endDateString);
    
    if (!endDate || isNaN(endDate.getTime())) {
      console.error('Invalid end date for return calculation:', endDateString);
      return null;
    }
    
    const dayOfWeek = endDate.getDay();
    let daysToAdd;
    
    // Calculate days to add based on day of week
    if (dayOfWeek === 0) {
      // Sunday → Monday (add 1 day)
      daysToAdd = 1;
    } else if (dayOfWeek === 5) {
      // Friday → Monday (add 3 days to skip weekend)
      daysToAdd = 3;
    } else if (dayOfWeek === 6) {
      // Saturday → Monday (add 2 days)
      daysToAdd = 2;
    } else {
      // Weekday → Next day (add 1 day)
      daysToAdd = 1;
    }
    
    const returnDate = new Date(endDate);
    returnDate.setDate(endDate.getDate() + daysToAdd);
    
    return this.formatDateISO(returnDate);
  }
}

// Make available globally
window.DateValidationService = DateValidationService;

// Export for use in modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DateValidationService;
}

console.log('✅ DateValidationService loaded', {
  minLeadTime: DateValidationService.BUSINESS_RULES.MIN_LEAD_TIME_DAYS + ' business days',
  cutoff: DateValidationService.BUSINESS_RULES.CUTOFF_HOUR_CT + ':00 ' + DateValidationService.BUSINESS_RULES.BUSINESS_TIMEZONE,
  earliestStart: DateValidationService.getEarliestStartDate(),
  maxRentalDays: DateValidationService.BUSINESS_RULES.MAX_RENTAL_DAYS + ' days'
});
